// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * <einvoice-rule-browser> - a virtualised, index-driven rule browser. Rolling out all 12,435 rows and letting
 * JS filter/hide them ( the approach of class-svtd-renderer.php's server-rendered rule browser, see STATUS.md )
 * does not scale: here only the catalog index and the ONE currently selected standard's rule set index ever
 * load, and the DOM never holds more rows than the viewport plus a small overscan buffer (VirtualList.mjs) -
 * regardless of whether that standard has 46 rules or 1,464.
 *
 * Usage:
 *
 *   <einvoice-rule-browser
 *     data-src="/wp-content/plugins/einvoice-viewer-technical-details/data/rule-catalog"
 *     data-default-standard="XRechnung-UBL"
 *     data-syntax-hint="UBL"
 *     data-height="480"
 *   ></einvoice-rule-browser>
 *
 * `data-src` is the ONLY thing that differs between hosts: the bundled,
 * same-origin `dist/rule-catalog/` copy in Technical Details, or `.../site-api/v1/rules` for the
 * invoicepress.de demo - RuleCatalogClient.mjs's fetch shape is identical either way.
 *
 * `data-syntax-hint` ("CII"/"UBL") lets a host that already knows which syntax the currently open
 * document uses bias the default standard pick without the component needing to know anything about
 * invoices itself.
 */

import { VirtualList } from './VirtualList.mjs';
import { RuleCatalogClient } from './RuleCatalogClient.mjs';
import { BROWSER_STYLE, escapeHtml, kitStyleLinks } from './browser-style.mjs';
import { setLanguage, t } from '../model/Labels.mjs';

/**
 * The labels of this component.
 *
 * ⚠️ **NO LANGUAGE TABLE OF ITS OWN** - see the same comment in `einvoice-field-browser.mjs`: such texts
 * would stand in no `.pot` and in no `.po`, and nobody would notice them missing. A fourth
 * language would need a code change.
 *
 * ⚠️ **THE SHAPE STAYS AN OBJECT WITH SHORT KEYS**, so that the twenty-five
 * places of use stay as they are; the texts come from `t()`.
 *
 * ⚠️ **`langNames` STAYS A NESTED OBJECT.** It names the language of a foreign-language
 * rule text in the notice below, and the three names are texts like any other - they go through
 * `t()` like the rest.
 */

function labels() {

	return {
		onlyEnglish:      t( 'This text is only available in English.' ),
		onlyIn:           t( 'This text is only available in %s.' ),
		langNames: {
			de: t( 'German' ),
			en: t( 'English' ),
			fr: t( 'French' ),
		},
		/*
		 * ⚠️ EVERY CONTROL HAS A VISIBLE LABEL - see the same comment in
		 * einvoice-field-browser.mjs. The rule set select needs one most: it lists names like
		 * "PEPPOL-CEN-EN16931-UBL (979)" without saying that these are rule sets.
		 */
		standardLabel:    t( 'Rule set' ),
		searchLabel:      t( 'Search' ),
		search:           t( 'Search rule ID, field or text …' ),
		fieldLabel:       t( 'Field' ),
		field:            t( 'BT/BG code' ),
		originLabel:      t( 'Origin' ),
		allOrigins:       t( 'All origins' ),
		jurisdictionLabel: t( 'Jurisdiction' ),
		allJurisdictions: t( 'All jurisdictions' ),
		severityLabel:    t( 'Severity' ),
		allSeverities:    t( 'All severities' ),
		allRulesets:      t( 'Load all rule sets' ),
		loading:          t( 'Loading …' ),
		title:            t( 'Business rules' ),
		colSeverity:      t( 'Severity' ),
		colId:            t( 'Rule ID' ),
		colStandard:      t( 'Rule set' ),
		colFields:        t( 'Fields' ),
		/*
		 * ⚠️ THE SHORT TEXTS OF THE LIST ARE NOT TRANSLATED, AND THE HEADING SAYS SO. The rule set index
		 * carries one short text per rule, in the language the rule set was written in: of the 9 271 short
		 * texts in Technical Details, 8 837 are English, 345 French and 89 German, and XRechnung-UBL alone
		 * mixes 29 English and 47 German ( measured 08.10.2026 ). The detail panel names the language of
		 * its text; the list has no room for that per row, so the column heading says it once on a page
		 * that is not English.
		 */
		colText:          t( 'Rule text' ),
		colTextOriginal:  t( 'Rule text ( original language )' ),
		empty:            t( 'No entry matches the filter.' ),
		/*
		 * ⚠️ THE SEVERITY IS DATA, BUT ITS NAME IS UI TEXT. The catalogue writes `fatal`, `error`,
		 * `warning` and `information`; shown as they are, they stand in English on a German page.
		 * The same four words in the validators read "Fehler", "Warnung" and "Hinweis".
		 */
		levels: {
			fatal:       t( 'Fatal' ),
			error:       t( 'Error' ),
			warning:     t( 'Warning' ),
			information: t( 'Information' ),
		},
		context:          t( 'Context (XPath)' ),
		test:             t( 'Test expression (XPath)' ),
		containedIn:      t( 'Contained in these rule sets' ),
		provenance:       t( 'Official source' ),
		license:          t( 'License' ),
		unverified:       t( 'not verified' ),
		close:            t( 'Close' ),
	};

}

/**
 * The language of the page, as far as this bundle can offer it.
 *
 * ⚠️ No fixed list `['de','en','fr']` here. `setLanguage()` decides by the available languages from
 * `i18n/index.json.js`.
 */

function documentLanguage() {
	return setLanguage( ( document.documentElement.lang || 'en' ).slice( 0, 2 ).toLowerCase() );
}

const ROW_HEIGHT = 32;

/*
 * ⚠️ ONLY THE COLUMNS ARE OWN STYLE; EVERYTHING ELSE IS browser-style.mjs. The column "Rule set" only
 * exists while more than one rule set is loaded ( "Load all rule sets" ); the class `multi` on the panel
 * switches to the template with five columns, so header and rows change together.
 */
/*
 * ⚠️ PIXELS, NOT `em`. The header row is set in 11 px and the rows in 13.5 px; the same `em` value gave
 * two different grids, and the headings stood up to 20 px left of their cells ( measured 08.10.2026 in
 * the first screenshot ). The first column fits the longest severity, „SCHWERWIEGEND“.
 */
const STYLE = BROWSER_STYLE + `
.br-panel { --cols: 132px 210px 130px minmax( 0, 1fr ); }
.br-panel.multi { --cols: 132px 210px 180px 130px minmax( 0, 1fr ); }
.br-panel:not( .multi ) .col-standard { display: none; }
`;

class EInvoiceRuleBrowser extends HTMLElement {

	connectedCallback() {

		if ( this.dataset.svRbReady === '1' )
			return;

		this.dataset.svRbReady = '1';

		// ⚠️ The language is remembered by the component itself, not only the labels. The rule texts come
		// from the catalogue data and carry their own language fields; without this setting the
		// display would have no way to prefer the chosen language and would always take English.
		// ⚠️ Set first, then label - `labels()` reads the configured language.
		this._lang = documentLanguage();
		this._t = labels();
		// ⚠️ `data-src-protected` is the rule catalogue of the "Extended Profiles" add-on; the host sets it
		// only when the add-on is active. See RuleCatalogClient.
		this._client = new RuleCatalogClient( this.dataset.src || '', this.dataset.srcProtected || '' );
		this._allRows = [];   // currently loaded rows (one or every standard, per user choice)
		this._filtered = [];

		const shade = this.attachShadow( { mode: 'open' } );

		// ⚠️ The kit from data-css first, then the own rules - see kitStyleLinks().
		kitStyleLinks( shade, this, STYLE );

		const root = document.createElement( 'div' );
		const tx = this._t;

		root.className = 'wrap';

		root.innerHTML = `
			<div class="panel br-panel rules-panel">
				<div class="panel-head"><h2>${ escapeHtml( tx.title ) }</h2><span class="count stats"></span></div>
				<div class="filterbar">
					<div class="field"><label for="br-f-standard">${ escapeHtml( tx.standardLabel ) }</label><select id="br-f-standard" class="sel f-standard"></select></div>
					<button type="button" class="btn f-all">${ escapeHtml( tx.allRulesets ) }</button>
					<div class="field br-grow"><label for="br-search">${ escapeHtml( tx.searchLabel ) }</label><input id="br-search" type="text" class="inp search" placeholder="${ escapeHtml( tx.search ) }"></div>
					<div class="field"><label for="br-f-origin">${ escapeHtml( tx.originLabel ) }</label><select id="br-f-origin" class="sel f-origin"><option value="">${ escapeHtml( tx.allOrigins ) }</option></select></div>
					<div class="field"><label for="br-f-jurisdiction">${ escapeHtml( tx.jurisdictionLabel ) }</label><select id="br-f-jurisdiction" class="sel f-jurisdiction"><option value="">${ escapeHtml( tx.allJurisdictions ) }</option></select></div>
					<div class="field"><label for="br-f-severity">${ escapeHtml( tx.severityLabel ) }</label><select id="br-f-severity" class="sel f-severity"><option value="">${ escapeHtml( tx.allSeverities ) }</option></select></div>
					<div class="field"><label for="br-f-field">${ escapeHtml( tx.fieldLabel ) }</label><input id="br-f-field" type="text" class="inp f-field" placeholder="${ escapeHtml( tx.field ) }"></div>
				</div>
				<div class="br-cols" aria-hidden="true"><span>${ escapeHtml( tx.colSeverity ) }</span><span>${ escapeHtml( tx.colId ) }</span><span class="col-standard">${ escapeHtml( tx.colStandard ) }</span><span>${ escapeHtml( tx.colFields ) }</span><span>${ escapeHtml( this._lang === 'en' ? tx.colText : tx.colTextOriginal ) }</span></div>
				<div class="br-list" style="height:${ parseInt( this.dataset.height || '480', 10 ) }px"></div>
				<div class="empty" hidden>${ escapeHtml( tx.empty ) }</div>
			</div>
			<div class="panel br-detail-panel" hidden></div>
		`;
		shade.appendChild( root );

		this._els = {
			standard: root.querySelector( '.f-standard' ),
			all: root.querySelector( '.f-all' ),
			search: root.querySelector( '.search' ),
			origin: root.querySelector( '.f-origin' ),
			jurisdiction: root.querySelector( '.f-jurisdiction' ),
			severity: root.querySelector( '.f-severity' ),
			field: root.querySelector( '.f-field' ),
			panel: root.querySelector( '.rules-panel' ),
			stats: root.querySelector( '.stats' ),
			list: root.querySelector( '.br-list' ),
			empty: root.querySelector( '.empty' ),
			detail: root.querySelector( '.br-detail-panel' ),
		};

		this._virtualList = new VirtualList( {
			container: this._els.list,
			rowHeight: ROW_HEIGHT,
			renderRow: ( item, index ) => this._renderRow( item, index ),
		} );

		this._wire();
		this._boot();

	}

	/** @return {number} DOM row count right now - for tests only. */
	get domRowCount() {
		return this._virtualList ? this._virtualList.domRowCount : 0;
	}

	async _boot() {

		// ⚠️ NEVER clear `this._els.list.innerHTML` here or anywhere else - VirtualList's constructor
		// already appended its sizer/viewport structure into this container when `new VirtualList(...)`
		// ran just above, and it never recreates that structure afterwards. Wiping the container's
		// innerHTML removes the sizer permanently; setItems() then has nothing to render into and every
		// row silently vanishes (measured in a browser check - domRowCount kept the right
		// number even though the list rendered empty, because the detached viewport still held its
		// rows, just no longer inside the visible container).
		this._els.stats.textContent = this._t.loading;

		const index = await this._client.index();

		for ( const entry of index ) {
			const opt = document.createElement( 'option' );
			opt.value = entry.standard;
			opt.textContent = `${ entry.standard } (${ entry.ruleCount })`;
			this._els.standard.appendChild( opt );
		}

		const preferred = this._pickDefaultStandard( index );

		if ( preferred )
			this._els.standard.value = preferred;

		this._applyUrlParams( index );

		await this._loadStandard( this._els.standard.value );

	}

	/**
	 * Context-aware pre-filtering via the page's own URL - the same mechanism
	 * class-svtd-plugin.php's ajax_popup() already documents ("query string from the calling button's
	 * own best-effort context detection"), now read by the component itself instead of svtd.js
	 * reaching into a server-rendered table that no longer exists for this browser.
	 *
	 * @param {Array<object>} index
	 */
	_applyUrlParams( index ) {

		let params;

		try {
			params = new URLSearchParams( window.location.search );
		} catch ( e ) {
			return;
		}

		const standard = params.get( 'svtd_standard' );

		if ( standard && index.some( ( e ) => e.standard === standard ) )
			this._els.standard.value = standard;

		if ( params.has( 'svtd_search' ) ) this._els.search.value = params.get( 'svtd_search' );
		if ( params.has( 'svtd_field' ) ) this._els.field.value = params.get( 'svtd_field' );
		// severity/family/jurisdiction option lists are only populated once a standard has loaded
		// (_rebuildFilterOptions) - stash the requested values and apply them there.
		this._pendingUrlFilters = {
			severity: params.get( 'svtd_severity' ) || '',
			family: params.get( 'svtd_family' ) || '',
			jurisdiction: params.get( 'svtd_jurisdiction' ) || '',
		};

	}

	/**
	 * The default standard is the current XRechnung version, with the syntax of the open document when
	 * there is one: prefer a host-supplied syntax hint (an open document's own CII/UBL), else the
	 * latest XRechnung entry the index actually has, else just the first entry.
	 */
	_pickDefaultStandard( index ) {

		const explicit = this.dataset.defaultStandard;

		if ( explicit && index.some( ( e ) => e.standard === explicit ) )
			return explicit;

		const hint = ( this.dataset.syntaxHint || '' ).toUpperCase();

		if ( hint ) {
			const bySyntax = index.find( ( e ) => e.standard.toLowerCase().includes( 'xrechnung' ) && ( e.syntax || '' ).toUpperCase() === hint );
			if ( bySyntax ) return bySyntax.standard;
		}

		const xrechnung = index.find( ( e ) => e.standard.toLowerCase().includes( 'xrechnung' ) );

		if ( xrechnung )
			return xrechnung.standard;

		return index[ 0 ]?.standard ?? null;

	}

	async _loadStandard( standard ) {

		this._els.stats.textContent = this._t.loading;

		/*
		 * ⚠️ A RULE SET THAT CANNOT BE LOADED SHOWS AS EMPTY, NOT AS AN UNCAUGHT REJECTION. The rule sets
		 * of the extensions live in the add-on, so an index and its files can come from two places, and a
		 * stale index next to a newer folder would otherwise leave "Loading …" standing with an error in the
		 * console.
		 */
		let data = {};

		try {
			data = await this._client.standard( standard );
		} catch ( e ) {
			data = {};
		}

		this._allRows = ( data.rules || [] ).map( ( r ) => ( { ...r, standard } ) );
		this._rebuildFilterOptions();
		this._applyFilters();

	}

	async _loadAllStandards() {

		this._els.stats.textContent = this._t.loading;

		const index = await this._client.index();
		const rows = [];

		for ( const entry of index ) {
			// ⚠️ One rule set that cannot be loaded must not stop the others - see _loadStandard().
			const data = await this._client.standard( entry.standard ).catch( () => ( {} ) );
			for ( const r of ( data.rules || [] ) )
				rows.push( { ...r, standard: entry.standard } );
			// show progress as each ruleset lands - a deliberate click already told the user this
			// loads the indexes one after another, not a silent blocking wait.
			this._els.stats.textContent = `${ this._t.loading } (${ rows.length })`;
		}

		this._allRows = rows;
		this._rebuildFilterOptions();
		this._applyFilters();

	}

	_rebuildFilterOptions() {

		const origins = new Set();
		const jurisdictions = new Set();
		const severities = new Set();

		for ( const r of this._allRows ) {
			if ( r.family ) origins.add( r.family );
			if ( r.jurisdiction ) jurisdictions.add( r.jurisdiction );
			if ( r.level ) severities.add( r.level );
		}

		this._fillSelect( this._els.origin, origins, this._t.allOrigins );
		this._fillSelect( this._els.jurisdiction, jurisdictions, this._t.allJurisdictions );
		this._fillSelect( this._els.severity, severities, this._t.allSeverities, this._t.levels );

		if ( this._pendingUrlFilters ) {
			const p = this._pendingUrlFilters;
			this._pendingUrlFilters = null;

			if ( p.severity && severities.has( p.severity ) ) this._els.severity.value = p.severity;
			if ( p.family && origins.has( p.family ) ) this._els.origin.value = p.family;
			if ( p.jurisdiction && jurisdictions.has( p.jurisdiction ) ) this._els.jurisdiction.value = p.jurisdiction;
		}

	}

	/**
	 * @param {HTMLSelectElement} select
	 * @param {Set<string>} values
	 * @param {string} allLabel
	 * @param {object} [names] Display name per value; the value itself where none is given. ⚠️ The
	 *        option's value stays the raw catalogue value, because the filter and the popup's
	 *        `svtd_severity` URL parameter compare against it.
	 */
	_fillSelect( select, values, allLabel, names = {} ) {

		const current = select.value;
		select.innerHTML = `<option value="">${ escapeHtml( allLabel ) }</option>`;

		for ( const v of Array.from( values ).sort() ) {
			const opt = document.createElement( 'option' );
			opt.value = v;
			opt.textContent = names[ v ] ?? v;
			select.appendChild( opt );
		}

		if ( Array.from( values ).includes( current ) )
			select.value = current;

	}

	_applyFilters() {

		const q = this._els.search.value.trim().toLowerCase();
		const field = this._els.field.value.trim().toUpperCase();
		const origin = this._els.origin.value;
		const jurisdiction = this._els.jurisdiction.value;
		const severity = this._els.severity.value;

		this._filtered = this._allRows.filter( ( r ) => {

			if ( origin && r.family !== origin ) return false;
			if ( jurisdiction && r.jurisdiction !== jurisdiction ) return false;
			if ( severity && r.level !== severity ) return false;
			if ( field && ! ( r.fields || [] ).some( ( f ) => f.toUpperCase().includes( field ) ) ) return false;

			if ( q ) {
				const haystack = [ r.id, r.text, ...( r.fields || [] ) ].join( ' ' ).toLowerCase();
				if ( ! haystack.includes( q ) ) return false;
			}

			return true;

		} );

		/*
		 * ⚠️ THE "RULE SET" COLUMN IS DETERMINED ONCE PER LIST, NOT PER ROW. Building a Set over every loaded
		 * row for each row drawn means, with all rule sets loaded, 9 271 entries for each of the forty rows
		 * on screen, on every scroll frame.
		 */
		this._els.panel.classList.toggle( 'multi', new Set( this._allRows.map( ( r ) => r.standard ) ).size > 1 );

		// ⚠️ A bare number next to the heading - see the same line in einvoice-field-browser.mjs.
		this._els.stats.textContent = String( this._filtered.length );
		this._els.empty.hidden = this._filtered.length > 0;
		this._virtualList.setItems( this._filtered );

	}

	_renderRow( item, index ) {

		const el = document.createElement( 'div' );
		el.className = ( index % 2 ) ? 'br-row br-alt' : 'br-row';

		const cls = ( item.level === 'fatal' || item.level === 'error' ) ? 'err' : ( item.level === 'warning' ? 'warn' : 'info' );
		const level = this._t.levels[ item.level ] ?? item.level ?? '';
		const fields = ( item.fields || [] ).join( ', ' );

		el.innerHTML = `
			<span><span class="badge ${ cls }">${ escapeHtml( level ) }</span></span>
			<span class="mono">${ escapeHtml( item.id ) }</span>
			<span class="mono muted col-standard">${ escapeHtml( item.standard ) }</span>
			<span class="mono br-code" title="${ escapeHtml( fields ) }">${ escapeHtml( fields ) }</span>
			<span title="${ escapeHtml( item.text || '' ) }">${ escapeHtml( item.text || '' ) }</span>
		`;

		el.addEventListener( 'click', () => this._openDetail( item ) );

		return el;

	}

	async _openDetail( item ) {

		const t = this._t;
		this._els.detail.hidden = false;
		this._els.detail.innerHTML = `<div class="br-loading">${ t.loading }</div>`;

		// ⚠️ Without its detail file the row's own short text is still shown - see the fallback below.
		const detail = await this._client.rule( item.standard, item.id ).catch( () => ( {} ) );

		/*
		 * ⚠️ CHOSEN LANGUAGE, THEN ENGLISH - AND THEN WHATEVER IS THERE, BUT WITH ITS NAME.
		 *
		 * `detail.description?.en ?? ?.de ?? ?.fr` would not be a fallback to English but English
		 * **always first**: a German page would show the English description although the German one
		 * stands next to it.
		 *
		 * ⚠️ **Dropping the third step entirely would be the next error.** Measured on
		 * 06.10.2026: 467 of 12 435 rule texts have no English at all - 378 French
		 * ( `br-fr-flux2-cii`, `br-fr-flux2-ubl`, `extended-ctc-fr-ubl` ) and 89 German
		 * ( `xrechnung-cii`, `xrechnung-ubl` ). A national rule set appears in its national
		 * language; an English text does not exist there. Whoever stops here shows nothing for
		 * these 467 rules and takes the only existing text away from the reader.
		 *
		 * The rule "never two languages on one page" does not forbid the foreign-language text,
		 * but the **unlabelled** one. That is why the code falls back and names the language - exactly
		 * what `Explainer/Renderer.php` does on the PHP side.
		 */

		const eigene = detail.description?.[ this._lang ] ?? '';

		let text     = eigene;
		let textLang = this._lang;

		if ( text === '' ) {

			for ( const versuch of [ 'en', 'de', 'fr' ] ) {

				if ( detail.description?.[ versuch ] ) {
					text     = detail.description[ versuch ];
					textLang = versuch;
					break;
				}

			}

		}

		if ( text === '' ) {
			// The short text of the index as the last option. The index names its language in `textLang`
			// when it is not English.
			text     = item.text ?? '';
			textLang = item.textLang ?? 'en';
		}

		/*
		 * ⚠️ A foreign-language text is named, not kept quiet. Without the notice the
		 * English sentence reads like a translation that does not exist - and a French sentence
		 * on a German page would look like an error, although it is the only existing text.
		 * The PHP side handles this the same way, see `notice_only_english`
		 * in `build-site.php` and `textLanguage()` in `Explainer/Renderer.php`.
		 */

		const hinweis = ( textLang !== this._lang && text !== '' )
			? `<p class="note warn">${ escapeHtml( ( t.onlyIn || t.onlyEnglish ).replace( '%s', t.langNames?.[ textLang ] ?? textLang ) ) }</p>`
			: '';
		const dups = ( detail.duplicates || [] )
			.map( ( d ) => `<li>${ escapeHtml( d.standard ) } — <span class="mono">${ escapeHtml( d.id ) }</span></li>` )
			.join( '' );

		const cls = ( item.level === 'fatal' || item.level === 'error' ) ? 'err' : ( item.level === 'warning' ? 'warn' : 'info' );

		this._els.detail.innerHTML = `
			<div class="panel-head"><span class="badge ${ cls }">${ escapeHtml( t.levels[ item.level ] ?? item.level ?? '' ) }</span><h2><span class="mono">${ escapeHtml( item.id ) }</span></h2><div class="panel-actions"><button type="button" class="icon-btn close" aria-label="${ escapeHtml( t.close ) }" title="${ escapeHtml( t.close ) }">&times;</button></div></div>
			<div class="panel-body pad br-detail">
				<p>${ escapeHtml( text ) }</p>
				${ hinweis }
				<details><summary>${ escapeHtml( t.context ) }</summary><pre>${ escapeHtml( detail.context || '' ) }</pre></details>
				<details><summary>${ escapeHtml( t.test ) }</summary><pre>${ escapeHtml( detail.test || '' ) }</pre></details>
				${ dups ? `<h4>${ escapeHtml( t.containedIn ) }</h4><ul>${ dups }</ul>` : '' }
				<dl>
					<dt>${ escapeHtml( t.provenance ) }</dt><dd>${ escapeHtml( detail.provenance?.body || '' ) }</dd>
					<dt>${ escapeHtml( t.license ) }</dt><dd>${ escapeHtml( detail.provenance?.license || t.unverified ) }</dd>
				</dl>
			</div>
		`;

		this._els.detail.querySelector( '.close' ).addEventListener( 'click', () => {
			this._els.detail.hidden = true;
		} );

	}

	_wire() {

		this._els.standard.addEventListener( 'change', () => this._loadStandard( this._els.standard.value ) );
		this._els.all.addEventListener( 'click', () => this._loadAllStandards() );

		let searchTimer = null;
		const onFilterChange = () => {
			window.clearTimeout( searchTimer );
			searchTimer = window.setTimeout( () => this._applyFilters(), 100 );
		};

		this._els.search.addEventListener( 'input', onFilterChange );
		this._els.field.addEventListener( 'input', onFilterChange );
		this._els.origin.addEventListener( 'change', () => this._applyFilters() );
		this._els.jurisdiction.addEventListener( 'change', () => this._applyFilters() );
		this._els.severity.addEventListener( 'change', () => this._applyFilters() );

	}

}

if ( typeof customElements !== 'undefined' && ! customElements.get( 'einvoice-rule-browser' ) )
	customElements.define( 'einvoice-rule-browser', EInvoiceRuleBrowser );
